import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { learningAPI } from '../lib/api';
import {
  GraduationCap,
  BookOpen,
  ChevronRight,
  CheckCircle2,
  Circle,
  ArrowLeft
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

export default function LearnPage() {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    async function loadModules() {
      try {
        const response = await learningAPI.getModules();
        setModules(response.data);
      } catch (error) {
        console.error('Failed to load modules:', error);
      } finally {
        setLoading(false);
      }
    }
    loadModules();
  }, []);

  const handleModuleSelect = async (moduleId) => {
    setLoading(true);
    try {
      const response = await learningAPI.getModule(moduleId);
      setModuleContent(response.data);
      setSelectedModule(moduleId);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setCompletedQuestions([]);
    } catch (error) {
      console.error('Failed to load module:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = async (answerIndex) => {
    if (answerResult) return;
    setSelectedAnswer(answerIndex);

    try {
      const response = await learningAPI.checkAnswer(selectedModule, currentQuestion, answerIndex);
      setAnswerResult(response.data);
      if (response.data.correct) {
        setCompletedQuestions(prev => [...prev, currentQuestion]);
      }
    } catch (error) {
      console.error('Failed to check answer:', error);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < moduleContent.quiz_questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setAnswerResult(null);
    }
  };

  const handleBack = () => {
    setSelectedModule(null);
    setModuleContent(null);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setAnswerResult(null);
    setCompletedQuestions([]);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="learn-loading">
        <div className="space-y-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="loading-pulse h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Module Content View
  if (selectedModule && moduleContent) {
    const quiz = moduleContent.quiz_questions;
    const currentQ = quiz[currentQuestion];
    const progress = ((completedQuestions.length / quiz.length) * 100);

    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="module-content">
        {/* Back button */}
        <Button variant="ghost" onClick={handleBack} className="mb-4" data-testid="back-to-modules">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('learn.back')}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <Badge variant="outline" className="w-fit mb-2">Module {moduleContent.order}</Badge>
                <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                  {moduleContent.title}
                </CardTitle>
                <CardDescription>{moduleContent.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="markdown-content" dangerouslySetInnerHTML={{
                    __html: moduleContent.content
                      .replace(/##\s(.+)/g, '<h2>$1</h2>')
                      .replace(/###\s(.+)/g, '<h3>$1</h3>')
                      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n\n/g, '</p><p>')
                      .replace(/^\d\.\s\*\*(.+?)\*\*\s-\s(.+)$/gm, '<li><strong>$1</strong> - $2</li>')
                  }} />
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Quiz */}
          <div>
            <Card data-testid="quiz-section">
              <CardHeader>
                <CardTitle className="text-lg">{t('learn.quiz')}</CardTitle>
                <CardDescription>
                  {t('learn.questionOf')} {currentQuestion + 1} {t('learn.of')} {quiz.length}
                </CardDescription>
                <Progress value={progress} className="mt-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="font-medium" data-testid="quiz-question">{currentQ.question}</p>

                <div className="space-y-2">
                  {currentQ.options.map((option, i) => {
                    let className = 'quiz-option';
                    if (selectedAnswer === i) className += ' selected';
                    if (answerResult) {
                      if (i === currentQ.correct) className += ' correct';
                      else if (selectedAnswer === i && !answerResult.correct) className += ' incorrect';
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => handleAnswerSelect(i)}
                        className={`${className} w-full text-left`}
                        disabled={answerResult !== null}
                        data-testid={`quiz-option-${i}`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>

                {answerResult && (
                  <div className={`p-3 rounded-lg ${answerResult.correct ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:text-red-400'}`}>
                    <p className="font-medium">
                      {answerResult.correct ? t('learn.correct') : t('learn.incorrect')}
                    </p>
                    <p className="text-sm mt-1">{answerResult.explanation}</p>
                  </div>
                )}

                {answerResult && currentQuestion < quiz.length - 1 && (
                  <Button onClick={handleNextQuestion} className="w-full" data-testid="next-question">
                    {t('learn.nextQuestion')}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}

                {answerResult && currentQuestion === quiz.length - 1 && (
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">{t('learn.moduleComplete')}</p>
                    <p className="text-sm text-muted-foreground">
                      {completedQuestions.length} {t('learn.of')} {quiz.length} {t('learn.score')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Modules List View
  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="learn-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <GraduationCap className="h-4 w-4" />
          <span>{t('learn.educationMode')}</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('learn.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('learn.subtitle')}
        </p>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="modules-grid">
        {modules.map((module) => (
          <Card
            key={module.id}
            className="module-card cursor-pointer"
            onClick={() => handleModuleSelect(module.id)}
            data-testid={`module-${module.id}`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge variant="outline">{t('learn.modulesPrefix')} {module.order}</Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
              <CardTitle className="text-xl mt-2">{module.title}</CardTitle>
              <CardDescription>{module.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-muted-foreground">
                <BookOpen className="h-4 w-4 mr-1" />
                <span>{t('learn.readingQuiz')}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Progress Note */}
      <Card className="mt-8 bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Circle className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h4 className="font-medium">{t('learn.progressStored.title')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('learn.progressStored.description')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
